import React, { useState, useMemo } from 'react'
import { Box, Button, Typography, Container } from '@mui/material'
import { styled } from '@mui/material/styles'

const CardContainer = styled(Box)(({ highlightcolor }) => ({
	position: 'relative',
	width: 45,
	height: 50,
	borderRadius: '6px',
	overflow: 'hidden',
	boxSizing: 'border-box',
	border: highlightcolor ? `3px solid ${highlightcolor}` : '2px solid transparent',
}))

const CardSprite = styled(Box)(({ clicked, row, col }) => ({
	width: '100%',
	height: '100%',
	backgroundImage:
		'url(https://x.boardgamearena.net/data/themereleases/current/games/tichu/240611-1230/img/tichu-cards.png)',
	backgroundSize: '1400px 600px',
	backgroundPosition: `-${col * (1400 / 14)}px -${row * (600 / 4)}px`,
	transition: 'all 0.2s ease-in-out',
	filter: clicked ? 'grayscale(100%) brightness(0.5)' : 'none',
	opacity: clicked ? 0.6 : 1,
	borderRadius: '6px',
	backgroundRepeat: 'no-repeat',
	position: 'absolute',
	top: 0,
	left: 0,
	'&:hover': {
		transform: clicked ? 'none' : 'scale(1.05)',
		boxShadow: clicked ? 'none' : '0 4px 20px rgba(0,0,0,0.3)',
	},
}))

const PlayerQuadrant = styled(Box)(({ playercolor, isactive }) => ({
	position: 'absolute',
	width: '50%',
	height: '50%',
	cursor: 'pointer',
	backgroundColor: isactive ? playercolor : 'transparent',
	opacity: isactive ? 0.8 : 0,
	transition: 'all 0.2s ease-in-out',
	'&:hover': {
		opacity: 0.6,
		backgroundColor: playercolor,
	},
	zIndex: 10,
}))

// Order in which the exchanged cards are given at the start of a round
const GIVE_ORDER = ['previous', 'partner', 'next']

const PlayerButton = styled(Button)(({ theme, playercolor }) => ({
	minWidth: '120px',
	margin: '4px',
	backgroundColor: playercolor,
	'&:hover': {
		backgroundColor: playercolor,
		filter: 'brightness(1.1)',
	},
}))

const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A']
const SUITS = ['BLACK', 'RED', 'GREEN', 'BLUE']

// Maximum number of disjoint bombs (four-of-a-kinds or straight flushes of 5+)
// that can still be formed from the cards not yet played.
const countPossibleBombs = (isCardPlayed) => {
	const available = SUITS.map((suit) => RANKS.map((rank) => !isCardPlayed(`${suit}-${rank}`)))
	const quadMask = RANKS.reduce(
		(mask, _, r) => (available.every((suit) => suit[r]) ? mask | (1 << r) : mask),
		0
	)
	let best = 0
	// Enumerate every subset of ranks used as four-of-a-kinds; the rest may form straight flushes.
	for (let subset = quadMask; ; subset = (subset - 1) & quadMask) {
		let bombs = 0
		for (let r = 0; r < RANKS.length; r++) if (subset & (1 << r)) bombs += 1
		for (const suit of available) {
			let run = 0
			for (let r = 0; r < RANKS.length; r++) {
				if (suit[r] && !(subset & (1 << r))) {
					run += 1
				} else {
					bombs += Math.floor(run / 5)
					run = 0
				}
			}
			bombs += Math.floor(run / 5)
		}
		if (bombs > best) best = bombs
		if (subset === 0) break
	}

	return best
}

const TichuCardsGame = () => {
	const players = [
		{ id: 'previous', name: 'Previous', color: '#9c27b0', position: 'top-left' },
		{ id: 'partner', name: 'Partner', color: '#ff9800', position: 'top-right' },
		{ id: 'next', name: 'Next', color: '#4caf50', position: 'bottom-right' },
		{ id: 'me', name: 'Me', color: '#2196f3', position: 'bottom-left' },
	]

	const quadrantPositions = {
		previous: { top: 0, left: 0 }, // top-left
		partner: { top: 0, right: 0 }, // top-right
		me: { bottom: 0, left: 0 }, // bottom-left
		next: { bottom: 0, right: 0 }, // bottom-right
	}

	// Define cards in the layout you want: Special cards, then Ace, King, Queen, etc.
	const cardLayout = [
		// Keep original sprite positions
		{ id: 'DRAGON', row: 0, col: 0, displayCol: 0 },
		{ id: 'PHOENIX', row: 1, col: 0, displayCol: 0 },
		{ id: 'DOG', row: 2, col: 0, displayCol: 0 },
		{ id: 'MAHJONG', row: 3, col: 0, displayCol: 0 },
		// 2s (sprite col 1, display col 13)
		{ id: 'BLACK-2', row: 0, col: 1, displayCol: 13 },
		{ id: 'RED-2', row: 1, col: 1, displayCol: 13 },
		{ id: 'GREEN-2', row: 2, col: 1, displayCol: 13 },
		{ id: 'BLUE-2', row: 3, col: 1, displayCol: 13 },
		// 3s (sprite col 2, display col 12)
		{ id: 'BLACK-3', row: 0, col: 2, displayCol: 12 },
		{ id: 'RED-3', row: 1, col: 2, displayCol: 12 },
		{ id: 'GREEN-3', row: 2, col: 2, displayCol: 12 },
		{ id: 'BLUE-3', row: 3, col: 2, displayCol: 12 },
		// 4s (sprite col 3, display col 11)
		{ id: 'BLACK-4', row: 0, col: 3, displayCol: 11 },
		{ id: 'RED-4', row: 1, col: 3, displayCol: 11 },
		{ id: 'GREEN-4', row: 2, col: 3, displayCol: 11 },
		{ id: 'BLUE-4', row: 3, col: 3, displayCol: 11 },
		// 5s (sprite col 4, display col 10)
		{ id: 'BLACK-5', row: 0, col: 4, displayCol: 10 },
		{ id: 'RED-5', row: 1, col: 4, displayCol: 10 },
		{ id: 'GREEN-5', row: 2, col: 4, displayCol: 10 },
		{ id: 'BLUE-5', row: 3, col: 4, displayCol: 10 },
		// 6s (sprite col 5, display col 9)
		{ id: 'BLACK-6', row: 0, col: 5, displayCol: 9 },
		{ id: 'RED-6', row: 1, col: 5, displayCol: 9 },
		{ id: 'GREEN-6', row: 2, col: 5, displayCol: 9 },
		{ id: 'BLUE-6', row: 3, col: 5, displayCol: 9 },
		// 7s (sprite col 6, display col 8)
		{ id: 'BLACK-7', row: 0, col: 6, displayCol: 8 },
		{ id: 'RED-7', row: 1, col: 6, displayCol: 8 },
		{ id: 'GREEN-7', row: 2, col: 6, displayCol: 8 },
		{ id: 'BLUE-7', row: 3, col: 6, displayCol: 8 },
		// 8s (sprite col 7, display col 7)
		{ id: 'BLACK-8', row: 0, col: 7, displayCol: 7 },
		{ id: 'RED-8', row: 1, col: 7, displayCol: 7 },
		{ id: 'GREEN-8', row: 2, col: 7, displayCol: 7 },
		{ id: 'BLUE-8', row: 3, col: 7, displayCol: 7 },
		// 9s (sprite col 8, display col 6)
		{ id: 'BLACK-9', row: 0, col: 8, displayCol: 6 },
		{ id: 'RED-9', row: 1, col: 8, displayCol: 6 },
		{ id: 'GREEN-9', row: 2, col: 8, displayCol: 6 },
		{ id: 'BLUE-9', row: 3, col: 8, displayCol: 6 },
		// 10s (sprite col 9, display col 5)
		{ id: 'BLACK-10', row: 0, col: 9, displayCol: 5 },
		{ id: 'RED-10', row: 1, col: 9, displayCol: 5 },
		{ id: 'GREEN-10', row: 2, col: 9, displayCol: 5 },
		{ id: 'BLUE-10', row: 3, col: 9, displayCol: 5 },
		// Jacks (sprite col 10, display col 4)
		{ id: 'BLACK-J', row: 0, col: 10, displayCol: 4 },
		{ id: 'RED-J', row: 1, col: 10, displayCol: 4 },
		{ id: 'GREEN-J', row: 2, col: 10, displayCol: 4 },
		{ id: 'BLUE-J', row: 3, col: 10, displayCol: 4 },
		// Queens (sprite col 11, display col 3)
		{ id: 'BLACK-Q', row: 0, col: 11, displayCol: 3 },
		{ id: 'RED-Q', row: 1, col: 11, displayCol: 3 },
		{ id: 'GREEN-Q', row: 2, col: 11, displayCol: 3 },
		{ id: 'BLUE-Q', row: 3, col: 11, displayCol: 3 },
		// Kings (sprite col 12, display col 2)
		{ id: 'BLACK-K', row: 0, col: 12, displayCol: 2 },
		{ id: 'RED-K', row: 1, col: 12, displayCol: 2 },
		{ id: 'GREEN-K', row: 2, col: 12, displayCol: 2 },
		{ id: 'BLUE-K', row: 3, col: 12, displayCol: 2 },
		// Aces (sprite col 13, display col 1)
		{ id: 'BLACK-A', row: 0, col: 13, displayCol: 1 },
		{ id: 'RED-A', row: 1, col: 13, displayCol: 1 },
		{ id: 'GREEN-A', row: 2, col: 13, displayCol: 1 },
		{ id: 'BLUE-A', row: 3, col: 13, displayCol: 1 },
	]

	// Track cards played by each player
	const [playerCards, setPlayerCards] = useState({
		me: new Set(),
		next: new Set(),
		partner: new Set(),
		previous: new Set(),
	})

	// Cards I passed at the start of the round: [{ cardId, playerId }], one per GIVE_ORDER entry
	const [givenCards, setGivenCards] = useState([])
	const [skipExchange, setSkipExchange] = useState(false)
	const isGivePhase = !skipExchange && givenCards.length < GIVE_ORDER.length

	// Track which player's cards are highlighted
	const [highlightedPlayer, setHighlightedPlayer] = useState(null)

	const totalCards = cardLayout.length
	const totalPlayedCards = Object.values(playerCards).reduce((sum, cards) => sum + cards.size, 0)
	const remainingCards = totalCards - totalPlayedCards

	const handleCardClick = (cardId, playerId) => {
		if (isGivePhase) {
			setGivenCards((prev) =>
				prev.some((g) => g.cardId === cardId)
					? prev.filter((g) => g.cardId !== cardId)
					: [...prev, { cardId, playerId: GIVE_ORDER[prev.length] }]
			)

			return
		}
		setPlayerCards((prev) => {
			const newPlayerCards = { ...prev }
			// Remove card from all players first
			Object.keys(newPlayerCards).forEach((pid) => {
				if (newPlayerCards[pid].has(cardId)) {
					newPlayerCards[pid] = new Set(
						[...newPlayerCards[pid]].filter((id) => id !== cardId)
					)
				}
			})
			// Add to clicked player if it wasn't already there
			if (!prev[playerId].has(cardId)) {
				newPlayerCards[playerId] = new Set([...newPlayerCards[playerId], cardId])
			}

			return newPlayerCards
		})
	}

	const handleReset = () => {
		setGivenCards([])
		setSkipExchange(false)
		setPlayerCards({
			me: new Set(),
			next: new Set(),
			partner: new Set(),
			previous: new Set(),
		})
	}

	const isCardPlayed = (cardId) => {
		return Object.values(playerCards).some((cards) => cards.has(cardId))
	}

	const getGivenColor = (cardId) => {
		const given = givenCards.find((g) => g.cardId === cardId)
		// Hide the border once the recipient has played all their cards
		if (!given || playerCards[given.playerId].size === 14) return null

		return players.find((p) => p.id === given.playerId).color
	}

	const getHighlightColor = (cardId) => {
		if (highlightedPlayer && playerCards[highlightedPlayer].has(cardId)) {
			const player = players.find((p) => p.id === highlightedPlayer)

			return player ? player.color : null
		}

		return null
	}

	const possibleBombs = useMemo(() => countPossibleBombs(isCardPlayed), [playerCards])

	const cardsByCol = cardLayout.reduce((acc, card) => {
		if (!acc[card.displayCol]) acc[card.displayCol] = []
		acc[card.displayCol].push(card)

		return acc
	}, {})

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Box sx={{ display: 'flex', gap: 4 }}>
				{/* Left side controls - All players */}
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '140px' }}>
					{players.map((player) => (
						<PlayerButton
							key={player.id}
							variant="contained"
							playercolor={player.color}
						>
							{player.name} ({14 - playerCards[player.id].size})
						</PlayerButton>
					))}
					<Button
						variant={skipExchange ? 'contained' : 'outlined'}
						color="secondary"
						sx={{ margin: '4px' }}
						disabled={givenCards.length > 0}
						onClick={() => setSkipExchange((prev) => !prev)}
					>
						Skip
					</Button>
					<Typography
						variant="subtitle1"
						sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}
					>
						Bombs left: {possibleBombs}
					</Typography>
				</Box>

				{/* Center - Cards and controls */}
				<Box sx={{ flex: 1 }}>
					<Box
						sx={{
							mb: 3,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							flexWrap: 'wrap',
							gap: 2,
						}}
					>
						<Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
							Cards: {remainingCards}
						</Typography>
						<Button
							variant="contained"
							color="primary"
							onClick={handleReset}
							size="large"
						>
							Reset
						</Button>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						{Object.keys(cardsByCol).map((colIndex) => (
							<Box
								key={colIndex}
								sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}
							>
								{cardsByCol[colIndex].map((card) => {
									const isClicked = isCardPlayed(card.id)

									return (
										<CardContainer
											key={card.id}
											highlightcolor={getGivenColor(card.id)}
										>
											<CardSprite
												clicked={isClicked}
												row={card.row}
												col={card.col}
											/>
											{/* Player quadrants */}
											{players.map((player) => (
												<PlayerQuadrant
													key={player.id}
													playercolor={player.color}
													isactive={
														playerCards[player.id].has(card.id) &&
														playerCards[player.id].size !== 14
													}
													sx={quadrantPositions[player.id]}
													onClick={() =>
														handleCardClick(card.id, player.id)
													}
												/>
											))}
										</CardContainer>
									)
								})}
							</Box>
						))}
					</Box>
				</Box>
			</Box>
		</Container>
	)
}

export default TichuCardsGame
